import { parse } from 'date-fns';
import { tz } from '@date-fns/tz';

/**
 * The scheduling half of a campaign config: a start/end pair in a fixed timezone.
 *
 * Split out from `FlashSaleRotation` so other campaign configs can reuse the window
 * selection below without also carrying flash-sale fields.
 */
export type CampaignWindow = {
  startDate: string;
  endDate: string;
};

export type FlashSaleRotation = CampaignWindow & {
  collectionName: string;
  campaignUrl: string;
};

const DEFAULT_PATTERN = 'yyyy-MM-dd HH:mm:ss';

const parseCampaignDate = (value: string, pattern: string, timezone: string): Date =>
  parse(value, pattern, new Date(), { in: tz(timezone) });

/**
 * Checks if the current time falls within a rotation's time range
 */
const isRotationActive = (rotation: CampaignWindow, now: Date, pattern: string, timezone: string): boolean => {
  const startDateTime = parseCampaignDate(rotation.startDate, pattern, timezone);
  const endDateTime = parseCampaignDate(rotation.endDate, pattern, timezone);

  return now >= startDateTime && now < endDateTime;
};

/**
 * Gets the active flash sale rotation based on current time in the given timezone
 *
 * @param rotations Array of flash sale rotations to check
 * @param timezone Timezone string (e.g. 'Asia/Singapore')
 * @param pattern Optional custom date-fns parse pattern. Defaults to 'yyyy-MM-dd HH:mm:ss'
 * @returns The active rotation if found, null otherwise
 */
export const getActiveFlashSaleRotation = <T extends CampaignWindow>(
  rotations: T[],
  timezone: string,
  pattern = DEFAULT_PATTERN,
): T | null => {
  const now = new Date();

  return rotations.find((rotation) => isRotationActive(rotation, now, pattern, timezone)) ?? null;
};

/**
 * Gets the next moment the active rotation changes — a rotation starting, or the current one ending.
 *
 * Consumers schedule their own re-read on this instant. A countdown only covers the end of a window
 * that is already running, so without this the gap before the first window never resolves itself.
 *
 * @param rotations Array of flash sale rotations to check
 * @param timezone Timezone string (e.g. 'Asia/Singapore')
 * @param pattern Optional custom date-fns parse pattern. Defaults to 'yyyy-MM-dd HH:mm:ss'
 * @returns The earliest boundary still ahead, or null once the whole schedule is in the past
 */
export const getNextFlashSaleBoundary = <T extends CampaignWindow>(
  rotations: T[],
  timezone: string,
  pattern = DEFAULT_PATTERN,
): Date | null => {
  const now = Date.now();

  const upcoming = rotations
    .flatMap((rotation) => [
      parseCampaignDate(rotation.startDate, pattern, timezone).getTime(),
      parseCampaignDate(rotation.endDate, pattern, timezone).getTime(),
    ])
    .filter((timestamp) => timestamp > now);

  return upcoming.length === 0 ? null : new Date(Math.min(...upcoming));
};
